<template>
	<div class="width-full">
		<div>
			<span class="text-neutral-900 text-body-m pb-2" :style="{ display: 'block' }">
				{{ $t('web.contact_modal.form.budget.label') }}
				<span class="text-error-500">*</span>
			</span>
		</div>
		<div class="relative">
			<Icon class="absolute arrow-icon" icon="chevron" :rotate="isOpen ? 'down' : ''" />
			<div
				:class="[
					'border-xs pa-4 rounded-2 width-full flex-grow-1 border-neutral-200 bg-neutral-25 budget-select',
					error ? 'border-error-500' : '',
				]"
				role="button"
				tabindex="0"
				@click="toggleOpen"
				@keydown.enter.prevent="toggleOpen"
				@keydown.space.prevent="toggleOpen"
				@keydown.esc="isOpen = false"
			>
				<span :class="budget ? 'text-neutral-900' : 'text-neutral-100'">
					{{ selectedLabel }}
				</span>
			</div>
			<div
				v-if="isOpen"
				class="absolute left-0 right-0 mt-2 rounded-2 bg-neutral-25 budget-options"
			>
				<button
					v-for="(budgetRange, key, i) in leadBudgets"
					:key="i"
					type="button"
					class="budget-option"
					@click="selectBudget({ key, range: budgetRange })"
				>
					{{ budgetRange.key }}
				</button>
			</div>
		</div>
		<div class="help-text-area text-body-m">
			<p v-if="error" class="text-error-500">{{ error }}</p>
			<p v-else-if="helperText" class="text-neutral-600">{{ helperText }}</p>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed, ref } from 'vue'
	import type { LeadFormBudget } from '~/actions/trip/lead'

	interface Props {
		error?: string
		helperText?: string
	}
	defineProps<Props>()

	const { t } = useI18n()

	const leadBudgets: Record<string, LeadFormBudget & { key: string }> = {
		lowerPrices: { min: 0, max: 1500, key: t('web.contact_modal.form.budget.option_1') },
		middlePrices: { min: 1500, max: 2500, key: t('web.contact_modal.form.budget.option_2') },
		higherPrices: { min: 2500, key: t('web.contact_modal.form.budget.option_3') },
	}

	const budget = defineModel<LeadFormBudget | undefined>('budget')
	const budgetKey = ref()
	const isOpen = ref(false)

	const selectedLabel = computed(() => {
		if (!budget.value) return t('web.contact_modal.form.budget.placeholder')
		const selected = Object.values(leadBudgets).find(
			(value) => value.max === budget.value?.max && value.min === budget.value?.min
		)
		return selected?.key || t('web.contact_modal.form.budget.placeholder')
	})

	const toggleOpen = () => {
		isOpen.value = !isOpen.value
	}

	const selectBudget = (option: { key: string; range: LeadFormBudget }) => {
		budgetKey.value = option.key
		budget.value = { min: option.range.min, max: option.range.max }
		isOpen.value = false
	}
</script>

<style scoped>
	.arrow-icon {
		top: 18px;
		right: 15px;
	}
	.budget-select {
		cursor: pointer;
	}

	.budget-options {
		box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
		max-height: 220px;
		overflow-y: auto;
		z-index: 10;
	}

	.budget-option {
		display: block;
		width: 100%;
		text-align: left;
		padding: 8px 12px;
		background: transparent;
		border: none;
		line-height: 1.5;
		cursor: pointer;
	}

	.budget-option:hover {
		background-color: var(--color-neutral-200);
	}

	.budget-option:focus {
		outline: none;
		background-color: var(--color-neutral-200);
	}
	.help-text-area {
		height: 20px;
		margin-top: 6px;
	}
</style>
